<template>
  <div v-if="user" class="min-h-screen bg-gray-100">
    <SidebarMenu
      :user="user"
      :isMenuOpen="isMenuOpen"
      @toggle-menu="toggleMenu"
      @close-menu="closeMenu"
      @logout="logout"
    />

    <div class="md:ml-64">
      <main class="p-4">
        <div class="p-4">
          <h1 class="text-xl font-bold">Facturación / Facturar Pagos</h1>
          <p class="text-gray-600 mb-4">Matrícula: {{ studentId }}</p>

          <!-- Botones de acción -->
          <div class="flex flex-wrap gap-2 mb-4">
            <button
              @click="solicitarFactura"
              :disabled="!selectedTicket || selectedTicket.invoiced !== 0 || invoiceLoading"
              class="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all duration-200"
              :class="selectedTicket && selectedTicket.invoiced === 0 && !invoiceLoading
                ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm cursor-pointer'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'"
            >
              <svg v-if="invoiceLoading" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 12 0 12 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {{ invoiceLoading ? 'Procesando...' : 'Solicitar Factura' }}
            </button>

            <button
              @click="mostrarAyuda"
              class="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-gray-600 hover:bg-gray-700 text-white shadow-sm transition-all duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Ayuda
            </button>
          </div>

          <div v-if="loading" class="flex justify-center my-8">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>

          <div v-else-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {{ error }}
          </div>

          <div v-else-if="tickets && tickets.length > 0">

            <!-- Layout principal: lista + detalle -->
            <div class="flex flex-col lg:flex-row gap-4">

              <!-- Panel izquierdo: lista de tickets -->
              <div class="flex-1">
                <h2 class="text-lg font-semibold mb-2">Registro de Pagos para Facturación:</h2>
                <p v-if="!selectedTicket" class="text-sm text-gray-500 mb-2 italic">Selecciona un ticket para ver su detalle o solicitar factura.</p>

                <!-- Vista móvil: tarjetas individuales -->
                <div class="grid grid-cols-1 gap-4 sm:hidden">
                  <div
                    v-for="(item, index) in sortedTickets"
                    :key="index"
                    @click="selectTicket(item)"
                    class="bg-white shadow overflow-hidden rounded-md p-4 border-l-4 cursor-pointer transition-all duration-150"
                    :class="[
                      item.invoiced === 1 ? 'border-green-500' : 'border-orange-500',
                      selectedTicket && selectedTicket.ticket === item.ticket
                        ? 'ring-2 ring-blue-500 bg-blue-50'
                        : item.invoiced === 1 ? 'bg-white hover:bg-green-50' : 'bg-orange-50 hover:bg-orange-100'
                    ]"
                  >
                    <div class="grid grid-cols-2 gap-y-2">
                      <div class="text-sm font-medium text-gray-500">Ticket:</div>
                      <div class="text-sm font-semibold">{{ item.ticket }}</div>

                      <div class="text-sm font-medium text-gray-500">Fecha de Pago:</div>
                      <div class="text-sm">{{ formatDate(item.paymentdate) }}</div>

                      <div class="text-sm font-medium text-gray-500">Método:</div>
                      <div class="text-sm">{{ item.method }}</div>

                      <div class="text-sm font-medium text-gray-500">Monto:</div>
                      <div class="text-sm font-semibold">${{ formatAmount(item.amount) }}</div>

                      <div class="text-sm font-medium text-gray-500">Estado:</div>
                      <div class="text-sm font-bold" :class="item.invoiced === 1 ? 'text-green-600' : 'text-orange-600'">
                        {{ item.invoiced === 1 ? 'Facturado' : 'Pendiente' }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Vista escritorio: tabla tradicional -->
                <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg hidden sm:block">
                  <table class="min-w-full divide-y divide-gray-300">
                    <thead class="bg-gray-50">
                      <tr>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Ticket</th>
                        <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Fecha de Pago</th>
                        <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Método</th>
                        <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Monto</th>
                        <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Estado</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200 bg-white text-sm font-medium text-gray-900">
                      <tr
                        v-for="(item, index) in sortedTickets"
                        :key="index"
                        @click="selectTicket(item)"
                        class="cursor-pointer transition-all duration-150"
                        :class="[
                          selectedTicket && selectedTicket.ticket === item.ticket
                            ? 'bg-blue-100 ring-2 ring-inset ring-blue-400'
                            : item.invoiced === 1 ? 'bg-green-50 hover:bg-green-100' : 'bg-orange-50 hover:bg-orange-100'
                        ]"
                      >
                        <td class="px-6 py-4 text-sm font-semibold text-gray-900">
                          {{ item.ticket }}
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-center text-sm text-gray-900">
                          {{ formatDate(item.paymentdate) }}
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-center text-sm text-gray-900">
                          {{ item.method }}
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-center text-sm font-semibold text-gray-900">
                          ${{ formatAmount(item.amount) }}
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-center text-sm font-bold" :class="item.invoiced === 1 ? 'text-green-600' : 'text-orange-600'">
                          {{ item.invoiced === 1 ? 'Facturado' : 'Pendiente' }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Panel derecho: detalle del ticket seleccionado -->
              <div v-if="selectedTicket" class="lg:w-96 w-full">
                <div class="bg-white shadow rounded-lg border border-gray-200 overflow-hidden">
                  <div class="px-4 py-3 border-b border-gray-200 flex items-center justify-between"
                    :class="selectedTicket.invoiced === 1 ? 'bg-green-50' : 'bg-orange-50'">
                    <div>
                      <h3 class="text-sm font-bold text-gray-800">Detalle del Ticket</h3>
                      <p class="text-xs text-gray-500 mt-0.5">{{ selectedTicket.ticket }}</p>
                    </div>
                    <button @click="clearSelection" class="text-gray-400 hover:text-gray-600 transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div v-if="detailLoading" class="flex justify-center py-8">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>

                  <div v-else-if="detailError" class="px-4 py-3 text-sm text-red-600">
                    {{ detailError }}
                  </div>

                  <div v-else-if="ticketDetail && ticketDetail.length > 0">
                    <table class="min-w-full divide-y divide-gray-200 text-sm">
                      <thead class="bg-gray-50">
                        <tr>
                          <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Periodo</th>
                          <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Concepto</th>
                          <th class="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-gray-100">
                        <tr v-for="(row, i) in ticketDetail" :key="i" class="hover:bg-gray-50">
                          <td class="px-3 py-2 text-gray-700 text-xs">{{ row.period }}</td>
                          <td class="px-3 py-2 text-gray-700 text-xs">{{ row.concept }}</td>
                          <td class="px-3 py-2 text-right font-medium text-gray-900 text-xs">${{ formatAmount(row.payment) }}</td>
                        </tr>
                      </tbody>
                      <tfoot class="bg-gray-50 border-t-2 border-gray-300">
                        <tr>
                          <td colspan="2" class="px-3 py-2 text-xs font-bold text-gray-700 uppercase">Total</td>
                          <td class="px-3 py-2 text-right text-sm font-bold text-gray-900">${{ formatAmount(detailTotal) }}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <div v-else class="px-4 py-6 text-center text-sm text-gray-500">
                    No hay detalles disponibles para este ticket.
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div v-else class="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            No hay pagos disponibles para facturar.
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import { getTicketsBilling, createInvoice } from "@/services/api";

export default {
  components: {
    SidebarMenu
  },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      tickets: null,
      loading: true,
      error: null,
      studentId: null,
      // Selección de ticket
      selectedTicket: null,
      // Detalle del ticket
      ticketDetail: null,
      detailLoading: false,
      detailError: null,
      // Estado de solicitud de factura
      invoiceLoading: false
    };
  },
  computed: {
    sortedTickets() {
      if (!this.tickets) return [];
      return [...this.tickets].sort((a, b) => {
        if (a.invoiced !== b.invoiced) return a.invoiced - b.invoiced;
        return new Date(b.paymentdate) - new Date(a.paymentdate);
      });
    },
    detailTotal() {
      if (!this.ticketDetail || this.ticketDetail.length === 0) return 0;
      return this.ticketDetail.reduce((sum, row) => sum + parseFloat(row.payment || 0), 0);
    }
  },
  async created() {
    await this.getUserFromBackend();
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '—';
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },

    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00';
      return parseFloat(amount).toLocaleString('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    async selectTicket(item) {
      this.selectedTicket = item;
      this.ticketDetail = null;
      this.detailError = null;
      this.detailLoading = true;

      try {
        const response = await getTicketsBilling({
          idstudent: this.studentId,
          filter: 'detail',
          idticket: item.ticket,
          from: '',
          to: ''
        });
        this.ticketDetail = response.data;
      } catch (error) {
        console.error("Error al obtener detalle del ticket:", error);
        this.detailError = "No se pudo cargar el detalle. Intente nuevamente.";
      } finally {
        this.detailLoading = false;
      }
    },

    clearSelection() {
      this.selectedTicket = null;
      this.ticketDetail = null;
      this.detailError = null;
    },

    async solicitarFactura() {
      if (!this.selectedTicket || this.selectedTicket.invoiced !== 0 || this.invoiceLoading) return;

      this.invoiceLoading = true;
      try {
        const response = await createInvoice({
          idstudent: this.studentId,
          idticket: this.selectedTicket.ticket
        });

        const data = response.data;

        if (data.success) {
          await this.$swal({
            title: 'Solicitud de Factura',
            html: `
              <div style="text-align: left; font-size: 14px; line-height: 1.7;">
                <p style="margin-bottom: 12px;">Estimado(a) alumno(a):</p>
                <p style="margin-bottom: 12px;">Para generar su factura, deberá continuar con el proceso de autofacturación en el siguiente enlace del proveedor autorizado:</p>
                <p style="text-align: center; margin-bottom: 16px;">
                  🌐 <a href="${data.url}" target="_blank" rel="noopener noreferrer"
                    style="color: #2563eb; font-weight: 600; word-break: break-all;">${data.url}</a>
                </p>
                <p style="margin-bottom: 8px;">Una vez dentro del sitio, el sistema le solicitará los siguientes datos:</p>
                <ul style="list-style: none; padding: 0; margin-bottom: 16px; background: #f3f4f6; border-radius: 8px; padding: 12px;">
                  <li style="margin-bottom: 6px;">🏢 <strong>Número de organización:</strong> ${data.organizacionId}</li>
                  <li style="margin-bottom: 6px;">🔐 <strong>Código de autofacturación:</strong> ${data.codigoAutofacturacion}</li>
                  <li>🧾 <strong>RFC:</strong> ${data.rfc}</li>
                </ul>
                <p style="margin-bottom: 12px;">Después de capturar correctamente la información, podrá acceder al sistema para completar la generación de su factura.</p>
                <p style="font-size: 12px; color: #6b7280; border-top: 1px solid #e5e7eb; padding-top: 10px; margin-top: 10px;">
                  ⚠️ Es importante verificar que los datos ingresados sean correctos, ya que la información proporcionada será utilizada para la emisión del comprobante fiscal.
                </p>
              </div>
            `,
            icon: 'success',
            confirmButtonText: 'Ir al sitio de facturación',
            showCancelButton: true,
            cancelButtonText: 'Cerrar',
            confirmButtonColor: '#2563eb',
            width: '600px'
          }).then((result) => {
            if (result.isConfirmed) {
              window.open(data.url, '_blank', 'noopener,noreferrer');
            }
          });

          // Recargar la lista de tickets para reflejar el cambio de estado
          await this.loadData();
          this.clearSelection();
        } else {
          this.$swal({
            title: 'Error',
            text: data.message || 'Ocurrió un error al solicitar la factura.',
            icon: 'error',
            confirmButtonColor: '#2563eb'
          });
        }
      } catch (error) {
        console.error("Error al solicitar factura:", error);
        this.$swal({
          title: 'Error',
          text: 'No se pudo procesar la solicitud. Por favor, intente nuevamente.',
          icon: 'error',
          confirmButtonColor: '#2563eb'
        });
      } finally {
        this.invoiceLoading = false;
      }
    },

    mostrarAyuda() {
      this.$swal({
        title: '📹 Video de Ayuda',
        text: 'Video no disponible por el momento.',
        icon: 'info',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#4b5563'
      });
    },

    async getUserFromBackend() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;

        if (this.user.email && this.user.email.match(/^a(\d+)@/)) {
          this.studentId = this.user.email.match(/^a(\d+)@/)[1];
          await this.loadData();
        } else {
          this.error = "No se pudo extraer el ID de estudiante del email.";
          this.loading = false;
        }
      } catch (error) {
        console.error("Error obteniendo usuario", error);
        this.$router.push('/login');
      }
    },

    async loadData() {
      try {
        const response = await getTicketsBilling({
          idstudent: this.studentId,
          filter: 'general',
          idticket: 0,
          from: '',
          to: ''
        });
        this.tickets = response.data;
        this.loading = false;
      } catch (error) {
        console.error("Error al obtener los tickets para facturación:", error);
        this.error = "Error al cargar los pagos para facturación. Por favor, intente nuevamente.";
        this.loading = false;
      }
    },

    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },

    closeMenu() {
      this.isMenuOpen = false;
    },

    async logout() {
      try {
        await axios.get('/sanctum/csrf-cookie');
        await new Promise(resolve => setTimeout(resolve, 100));

        const response = await axios.post('/logout', {}, {
          withCredentials: true,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        console.log(response);

        document.cookie.split(";").forEach(function(c) {
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });

        this.$router.push('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error.response?.data || error);
      }
    }
  }
};
</script>